import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { MapPicker } from "../components/MapPicker";
import { Seo } from "../components/Seo";
import { ShareButton } from "../components/ShareButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SubmitTori() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [story, setStory] = useState("");
  const [locationText, setLocationText] = useState("");
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [photos, setPhotos] = useState([]); // {path, url}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (photos.length + files.length > 8) {
      toast.error("Maximaal 8 foto's.");
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${API}/tori/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((prev) => [...prev, data]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Uploaden mislukt.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (path) => setPhotos((prev) => prev.filter((p) => p.path !== path));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !story.trim()) {
      toast.error("Vul je naam en verhaal in.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/tori`, {
        name,
        email,
        story,
        photos: photos.map((p) => p.path),
        location_text: locationText,
        lat: coords.lat,
        lng: coords.lng,
      });
      navigate("/tori-verstuurd");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Er ging iets mis.");
    } finally {
      setSubmitting(false);
    }
  };

  const onPick = (v) => {
    setCoords({ lat: v.lat, lng: v.lng });
    if (v.address && !locationText) setLocationText(v.address);
  };

  return (
    <div className="max-w-[800px] mx-auto w-full px-6 py-12">
      <Seo
        title="Deel jouw Tori | De Zeeuwse Tori"
        description="Vertel jouw verhaal en zet jezelf op de Zeeuwse Tori kaart. Deel je favoriete plek in Zeeland."
        image="og-deel-tori.jpg"
        path="/deel-jouw-tori"
      />
      <div className="text-center mb-10">
        <h1 className="text-4xl sm:text-5xl font-[700] text-[#0D3B4C] tracking-tight">Deel jouw Tori</h1>
        <p className="mt-3 text-lg md:text-xl font-[600] text-[#0D3B4C]">
          Vertel je verhaal en zet jouw plek op de kaart
        </p>
        <ShareButton slug="deel-jouw-tori" title="Deel jouw Tori | De Zeeuwse Tori" label="Deel deze pagina" className="mt-4 inline-flex items-center gap-2 text-[#1B6B2E] font-[700] text-sm uppercase tracking-wider hover:text-[#0D3B4C] transition-colors" />
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="submit-tori-form">
        <div>
          <label className="block font-[600] text-[#0D3B4C] mb-1">Naam *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="tori-name"
            className="w-full border-2 border-[#0D3B4C] px-4 py-3 focus:border-[#B71C1C] focus:outline-none"
          />
        </div>

        <div>
          <label className="block font-[600] text-[#0D3B4C] mb-1">E-mail (optioneel)</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="tori-email"
            placeholder="Voor een bedankje als je verhaal live gaat"
            className="w-full border-2 border-[#0D3B4C] px-4 py-3 focus:border-[#B71C1C] focus:outline-none"
          />
        </div>

        <div>
          <label className="block font-[600] text-[#0D3B4C] mb-1">Jouw verhaal *</label>
          <textarea
            value={story}
            onChange={(e) => setStory(e.target.value)}
            data-testid="tori-story"
            rows={5}
            className="w-full border-2 border-[#0D3B4C] px-4 py-3 focus:border-[#B71C1C] focus:outline-none"
          />
        </div>

        <div>
          <label className="block font-[600] text-[#0D3B4C] mb-1">Foto's (optioneel, max 8)</label>
          <label
            data-testid="tori-photo-label"
            className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#0D3B4C] font-[700] px-4 py-3 cursor-pointer hover:bg-[#d4ecd7] transition-colors"
          >
            <Upload size={18} /> {uploading ? "Uploaden..." : "Foto's kiezen"}
            <input type="file" accept="image/*" multiple onChange={onFiles} data-testid="tori-photos" className="hidden" />
          </label>
          {photos.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
              {photos.map((p) => (
                <div key={p.path} className="relative aspect-square bg-[#E8F5E9]">
                  <img src={`${BACKEND}${p.url}`} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(p.path)}
                    data-testid="remove-photo"
                    className="absolute top-1 right-1 bg-[#B71C1C] text-white w-6 h-6 flex items-center justify-center"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block font-[600] text-[#0D3B4C] mb-1">Locatie (plaats of adres)</label>
          <input
            value={locationText}
            onChange={(e) => setLocationText(e.target.value)}
            data-testid="tori-location-text"
            placeholder="bijv. Middelburg"
            className="w-full border-2 border-[#0D3B4C] px-4 py-3 focus:border-[#B71C1C] focus:outline-none mb-4"
          />
          <MapPicker value={coords} onChange={onPick} />
          {coords.lat && (
            <p className="text-sm font-[600] text-[#1B6B2E] mt-2" data-testid="picked-coords">
              Gekozen locatie: {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={submitting || uploading}
          data-testid="tori-submit"
          className="w-full bg-[#B71C1C] text-white font-[700] uppercase tracking-wider py-4 hover:bg-[#8E1515] transition-colors disabled:opacity-50"
        >
          {submitting ? "Versturen..." : "Verstuur mijn Tori"}
        </button>
        <p className="text-xs text-center text-[#0D3B4C]">
          Je verhaal wordt eerst beoordeeld voordat het live gaat.
        </p>
      </form>
    </div>
  );
}
